import scheduleMessageService from "@/services/scheduleMessageService";

const state = {
	scheduledMessages: [],
	loading: false,
	error: null,
	currentMessage: null
};

const getters = {
	getAllMessages: state => state.scheduledMessages,
	getMessageById: state => id => state.scheduledMessages.find(msg => msg.id === id),
	getMessagesByPhoneNumber: state => phoneNumber => state.scheduledMessages.filter(msg => msg.toPhoneNumber === phoneNumber),
	isLoading: state => state.loading,
	getError: state => state.error,
	getCurrentMessage: state => state.currentMessage
};

const mutations = {
	setScheduledMessages(state, messages) {
		state.scheduledMessages = messages ?? [];
	},
	addScheduledMessage(state, message) {
		state.scheduledMessages.push(message);
	},
	updateScheduledMessage(state, updatedMessage) {
		const index = state.scheduledMessages.findIndex(msg => msg.id === updatedMessage.id);

		if (index !== -1) {
			state.scheduledMessages.splice(index, 1, updatedMessage);
		}
	},
	removeScheduledMessage(state, messageId) {
		state.scheduledMessages = state.scheduledMessages.filter(msg => msg.id !== messageId);
	},
	setLoading(state, status) {
		state.loading = status;
	},
	setError(state, error) {
		state.error = error;
	},
	setCurrentMessage(state, message) {
		state.currentMessage = message;
	},
	clearError(state) {
		state.error = null;
	}
};

const actions = {
	async fetchScheduledMessages({ commit }) {
		commit("setLoading", true);
		commit("clearError");
		try {
			const messages = await scheduleMessageService.getAll();
			// Keep ISO format for reliable parsing across all locales

			commit("setScheduledMessages", messages);
			return messages;
		} catch (error) {
			commit("setError", error.message);
			throw error;
		} finally {
			commit("setLoading", false);
		}
	},

	async createScheduledMessage({ commit }, messageData) {
		commit("setLoading", true);
		commit("clearError");
		try {
			const newMessage = await scheduleMessageService.create(messageData);

			commit("addScheduledMessage", newMessage);
			return newMessage;
		} catch (error) {
			commit("setError", error.message);
			throw error;
		} finally {
			commit("setLoading", false);
		}
	},

	async updateScheduledMessage({ commit }, { messageId, updateData }) {
		commit("setLoading", true);
		commit("clearError");
		try {
			const updatedMessage = await scheduleMessageService.update(messageId, updateData);

			// commit("updateScheduledMessage", updatedMessage);
			return updatedMessage;
		} catch (error) {
			commit("setError", error.message);
			throw error;
		} finally {
			commit("setLoading", false);
		}
	},

	async removeScheduledMessage({ commit }, messageId) {
		commit("setLoading", true);
		commit("clearError");
		try {
			await scheduleMessageService.remove(messageId);
			// commit("removeScheduledMessage", messageId);
		} catch (error) {
			commit("setError", error.message);
			throw error;
		} finally {
			commit("setLoading", false);
		}
	},

	async fetchMessageById({ commit }, messageId) {
		commit("setLoading", true);
		commit("clearError");
		try {
			const message = await scheduleMessageService.getById(messageId);

			commit("setCurrentMessage", message);
			return message;
		} catch (error) {
			commit("setError", error.message);
			throw error;
		} finally {
			commit("setLoading", false);
		}
	},

	clearCurrentMessage({ commit }) {
		commit("setCurrentMessage", null);
	},

	clearError({ commit }) {
		commit("clearError");
	},

	async fetchMessagesByPhone({ commit }, phoneNumber) {
		commit("setLoading", true);
		commit("clearError");
		try {
			const messages = await scheduleMessageService.findByPhone(phoneNumber);

			// Keep ISO format for reliable parsing across all locales
			// Sorting is done in the component using compareScheduledDates
			commit("setScheduledMessages", messages);
			return messages;
		} catch (error) {
			commit("setError", error.message);
			throw error;
		} finally {
			commit("setLoading", false);
		}
	}
};

export default {
	namespaced: true,
	state,
	getters,
	mutations,
	actions
};
